import { useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, unwrap } from "../../lib/api";
import { meQuery } from "../../lib/me";
import { useApiMutation } from "../../lib/mutation";
import { Badge } from "../../ui/Badge";
import { Button } from "../../ui/Button";
import { Input } from "../../ui/Input";
import { toast } from "../../ui/toast";

type Organization = { id: string; name: string; slug: string; members: number; actions: number };

/** One organization on the instance list, with an in-place fix of its names (#232). */
export function OrganizationRow({
  organization,
  onSaved,
}: {
  organization: Organization;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<{ name: string; slug: string } | null>(null);
  const save = useApiMutation(
    (json: { name: string; slug: string }) =>
      unwrap(
        api.platform.organizations[":organizationId"].$patch({
          param: { organizationId: organization.id },
          json,
        }),
      ),
    {
      onSuccess: () => {
        toast(t("common.saved"));
        setDraft(null);
        onSaved();
        // The header shows the caller's own name and organizations.
        void queryClient.invalidateQueries({ queryKey: meQuery.queryKey });
      },
    },
  );
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (draft) save.run(draft);
  };

  if (draft)
    return (
      <li className="px-4 py-2">
        <form onSubmit={submit} className="flex flex-wrap items-center gap-2">
          <Input
            aria-label={t("organizations.name")}
            required
            minLength={2}
            className="min-w-48 flex-1"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />
          <Input
            aria-label={t("organizations.slug")}
            required
            pattern="[a-z0-9][a-z0-9.\-]{1,59}"
            className="w-48"
            value={draft.slug}
            onChange={(e) => setDraft({ ...draft, slug: e.target.value.toLowerCase() })}
          />
          <Button type="submit" size="sm" variant="primary" disabled={save.isPending}>
            {t("common.save")}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setDraft(null)}>
            {t("common.cancel")}
          </Button>
        </form>
      </li>
    );
  return (
    <li className="flex flex-wrap items-center gap-3 px-4 py-2">
      <span className="font-medium">{organization.name}</span>
      <span className="text-zinc-400">{organization.slug}</span>
      <Badge className="ml-auto">
        {t("organizations.members", { count: organization.members })}
      </Badge>
      <Badge>{t("organizations.actions", { count: organization.actions })}</Badge>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => setDraft({ name: organization.name, slug: organization.slug })}
      >
        {t("common.edit")}
      </Button>
    </li>
  );
}
